import { useEffect, useMemo, useRef, useState } from "react";
import type { ErrorLogDTO, ErrorLogsSummary } from "@tw-pulse/shared";
import { api, ApiClientError } from "@/lib/api";
import { useConnectionContext } from "@/lib/connectionContext";
import { useInfiniteScroll } from "@/lib/useInfiniteScroll";
import { Button } from "@/components/ui/Button";
import { SummaryCards, type SummaryCard } from "@/components/SummaryCards";
import { ExternalLinkIcon, RefreshIcon } from "@/lib/icons";

type SummaryState =
  | { status: "loading" }
  | { status: "ready"; data: ErrorLogsSummary }
  | { status: "error"; message: string };

type LogLevelFilter = "all" | "error" | "warning" | "notice" | "info";

type State =
  | { status: "loading" }
  | { status: "ready"; rows: ErrorLogDTO[]; nextCursor: string | null }
  | { status: "error"; message: string };

const PAGE_SIZE = 25;

/**
 * Twilio Alerts (ErrorLog) browser. Server-side cursor pagination over
 * (dateCreated DESC, _id DESC). Honors the global account-context selector
 * and supports log-level + errorCode + resourceSid search filters.
 *
 * Read-only — drill-out goes to Twilio Console (or the alert's `moreInfo`
 * doc URL when present).
 */
export function AlertsPage() {
  const ctx = useConnectionContext();
  const scope = ctx.allSelected ? undefined : ctx.selectedIds;
  const scopeKey = scope ? scope.join(",") : "all";

  const [state, setState] = useState<State>({ status: "loading" });
  const [summary, setSummary] = useState<SummaryState>({ status: "loading" });
  const [logLevelFilter, setLogLevelFilter] = useState<LogLevelFilter>("all");

  // Summary refetches only on scope change.
  useEffect(() => {
    if (ctx.status !== "ready") return;
    let cancelled = false;
    setSummary({ status: "loading" });
    void api.errorLogs
      .summary(scope)
      .then((data) => {
        if (!cancelled) setSummary({ status: "ready", data });
      })
      .catch((err) => {
        if (!cancelled) {
          setSummary({
            status: "error",
            message: err instanceof ApiClientError ? err.message : "Failed to load",
          });
        }
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.status, scopeKey]);
  const [errorCodeInput, setErrorCodeInput] = useState("");
  const [errorCodeApplied, setErrorCodeApplied] = useState<number | null>(null);
  const [searchInput, setSearchInput] = useState("");
  const [searchApplied, setSearchApplied] = useState("");
  const [loadingMore, setLoadingMore] = useState(false);

  // Debounce search and errorCode inputs.
  useEffect(() => {
    const handle = window.setTimeout(() => {
      setSearchApplied(searchInput.trim());
    }, 300);
    return () => window.clearTimeout(handle);
  }, [searchInput]);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      const trimmed = errorCodeInput.trim();
      if (trimmed === "") {
        setErrorCodeApplied(null);
      } else {
        const n = Number(trimmed);
        setErrorCodeApplied(Number.isFinite(n) ? n : null);
      }
    }, 300);
    return () => window.clearTimeout(handle);
  }, [errorCodeInput]);

  const reqIdRef = useRef(0);
  useEffect(() => {
    if (ctx.status !== "ready") return;
    const reqId = ++reqIdRef.current;
    setState({ status: "loading" });

    void api.errorLogs
      .list(scope, {
        logLevel: logLevelFilter === "all" ? undefined : logLevelFilter,
        errorCode: errorCodeApplied ?? undefined,
        search: searchApplied || undefined,
        limit: PAGE_SIZE,
      })
      .then((res) => {
        if (reqIdRef.current !== reqId) return;
        setState({
          status: "ready",
          rows: res.errorLogs,
          nextCursor: res.nextCursor,
        });
      })
      .catch((err) => {
        if (reqIdRef.current !== reqId) return;
        setState({
          status: "error",
          message: err instanceof ApiClientError ? err.message : "Failed to load",
        });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.status, scopeKey, logLevelFilter, errorCodeApplied, searchApplied]);

  function loadMore() {
    if (state.status !== "ready" || !state.nextCursor || loadingMore) return;
    const cursor = state.nextCursor;
    const reqId = reqIdRef.current;
    setLoadingMore(true);
    void api.errorLogs
      .list(scope, {
        logLevel: logLevelFilter === "all" ? undefined : logLevelFilter,
        errorCode: errorCodeApplied ?? undefined,
        search: searchApplied || undefined,
        limit: PAGE_SIZE,
        cursor,
      })
      .then((res) => {
        if (reqIdRef.current !== reqId) return;
        setState((prev) =>
          prev.status === "ready"
            ? {
                status: "ready",
                rows: [...prev.rows, ...res.errorLogs],
                nextCursor: res.nextCursor,
              }
            : prev,
        );
      })
      .catch(() => {
        // Soft-fail on load more — keep existing rows visible.
      })
      .finally(() => {
        if (reqIdRef.current === reqId) setLoadingMore(false);
      });
  }

  function refresh() {
    if (ctx.status !== "ready") return;
    setState({ status: "loading" });
    const reqId = ++reqIdRef.current;
    void api.errorLogs
      .list(scope, {
        logLevel: logLevelFilter === "all" ? undefined : logLevelFilter,
        errorCode: errorCodeApplied ?? undefined,
        search: searchApplied || undefined,
        limit: PAGE_SIZE,
      })
      .then((res) => {
        if (reqIdRef.current !== reqId) return;
        setState({
          status: "ready",
          rows: res.errorLogs,
          nextCursor: res.nextCursor,
        });
      })
      .catch((err) => {
        if (reqIdRef.current !== reqId) return;
        setState({
          status: "error",
          message: err instanceof ApiClientError ? err.message : "Failed to load",
        });
      });
  }

  const connNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of ctx.connections) {
      map.set(c.id, c.friendlyName ?? c.accountSid);
    }
    return map;
  }, [ctx.connections]);

  const filtersActive =
    logLevelFilter !== "all" || errorCodeApplied !== null || searchApplied.length > 0;

  const hasMore = state.status === "ready" && !!state.nextCursor;
  const sentinelRef = useInfiniteScroll<HTMLDivElement>({
    enabled: hasMore && !loadingMore,
    onLoadMore: loadMore,
  });

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[11.5px] font-semibold uppercase tracking-[0.16em] text-brand-600 dark:text-brand-400">
            Operations
          </p>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-50">
            Alerts
          </h1>
          <p className="mt-1 text-[13.5px] text-zinc-600 dark:text-zinc-400">
            Twilio platform alerts (the customer-facing "Error Logs" surface) synced from your
            connected accounts. Newest first.
          </p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={refresh}
          disabled={ctx.status !== "ready" || state.status === "loading"}
        >
          <RefreshIcon size={14} />
          Refresh
        </Button>
      </header>

      <SummaryCards
        loading={summary.status === "loading"}
        error={summary.status === "error" ? summary.message : null}
        cards={summary.status === "ready" ? buildAlertsSummaryCards(summary.data) : []}
      />

      <Toolbar
        logLevel={logLevelFilter}
        onLogLevelChange={setLogLevelFilter}
        errorCode={errorCodeInput}
        onErrorCodeChange={setErrorCodeInput}
        search={searchInput}
        onSearchChange={setSearchInput}
        disabled={state.status === "loading"}
      />

      {state.status === "loading" && <SkeletonRows />}
      {state.status === "error" && (
        <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
          {state.message}
        </p>
      )}
      {state.status === "ready" && state.rows.length === 0 && !filtersActive && <EmptyState />}
      {state.status === "ready" && state.rows.length === 0 && filtersActive && (
        <FilteredEmpty
          onClear={() => {
            setLogLevelFilter("all");
            setErrorCodeInput("");
            setErrorCodeApplied(null);
            setSearchInput("");
            setSearchApplied("");
          }}
        />
      )}
      {state.status === "ready" && state.rows.length > 0 && (
        <>
          <Table rows={state.rows} connNameById={connNameById} />
          <div ref={sentinelRef} className="flex justify-center pt-2">
            {state.nextCursor ? (
              <p className="text-[12px] text-zinc-500">
                {loadingMore ? (
                  <>Loading more…</>
                ) : (
                  <>Showing {state.rows.length.toLocaleString("en-US")} · scroll for more</>
                )}
              </p>
            ) : (
              <p className="text-[12px] text-zinc-500">
                {state.rows.length.toLocaleString("en-US")} alert
                {state.rows.length === 1 ? "" : "s"} · end of results
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

const LOG_LEVELS: Array<{ value: LogLevelFilter; label: string }> = [
  { value: "all", label: "All" },
  { value: "error", label: "Error" },
  { value: "warning", label: "Warning" },
  { value: "notice", label: "Notice" },
  { value: "info", label: "Info" },
];

function Toolbar({
  logLevel,
  onLogLevelChange,
  errorCode,
  onErrorCodeChange,
  search,
  onSearchChange,
  disabled,
}: {
  logLevel: LogLevelFilter;
  onLogLevelChange: (l: LogLevelFilter) => void;
  errorCode: string;
  onErrorCodeChange: (c: string) => void;
  search: string;
  onSearchChange: (s: string) => void;
  disabled: boolean;
}) {
  return (
    <div className="flex flex-wrap items-center gap-3">
      <div
        role="radiogroup"
        aria-label="Log level filter"
        className="inline-flex flex-wrap items-center gap-0.5 rounded-md border border-zinc-200 bg-zinc-50 p-0.5 dark:border-zinc-800 dark:bg-zinc-900/40"
      >
        {LOG_LEVELS.map((l) => (
          <button
            key={l.value}
            type="button"
            role="radio"
            aria-checked={logLevel === l.value}
            disabled={disabled}
            onClick={() => onLogLevelChange(l.value)}
            className={
              "rounded-[5px] px-2.5 py-1 text-[12px] font-medium transition " +
              (logLevel === l.value
                ? "bg-white text-zinc-900 shadow-sm dark:bg-zinc-800 dark:text-zinc-50"
                : "text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-100")
            }
          >
            {l.label}
          </button>
        ))}
      </div>
      <input
        type="text"
        inputMode="numeric"
        value={errorCode}
        onChange={(e) => onErrorCodeChange(e.target.value)}
        placeholder="Error code (e.g. 30007)"
        className="h-9 w-[180px] rounded-md border border-zinc-200 bg-white px-3 text-[13px] text-zinc-900 placeholder:text-zinc-400 outline-none transition focus-visible:border-brand-500 focus-visible:bg-white focus-visible:shadow-ring dark:border-zinc-800 dark:bg-zinc-950/40 dark:text-zinc-100 dark:placeholder:text-zinc-600 dark:focus-visible:border-brand-500/70 dark:focus-visible:bg-zinc-950/60"
      />
      <input
        type="search"
        value={search}
        onChange={(e) => onSearchChange(e.target.value)}
        placeholder="Search by Resource SID (CA…, SM…, etc.)"
        className="h-9 min-w-[220px] max-w-md flex-1 rounded-md border border-zinc-200 bg-white px-3 text-[13px] text-zinc-900 placeholder:text-zinc-400 outline-none transition focus-visible:border-brand-500 focus-visible:bg-white focus-visible:shadow-ring dark:border-zinc-800 dark:bg-zinc-950/40 dark:text-zinc-100 dark:placeholder:text-zinc-600 dark:focus-visible:border-brand-500/70 dark:focus-visible:bg-zinc-950/60"
      />
    </div>
  );
}

// ---------------------------------------------------------------------------

function Table({ rows, connNameById }: { rows: ErrorLogDTO[]; connNameById: Map<string, string> }) {
  return (
    <div className="overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-card-light dark:border-zinc-800/80 dark:bg-zinc-900/40 dark:shadow-none dark:ring-1 dark:ring-inset dark:ring-white/[0.02]">
      <table className="w-full text-left text-sm">
        <thead className="bg-zinc-50 text-[11px] font-medium uppercase tracking-wider dark:bg-zinc-900/60 text-zinc-500">
          <tr>
            <th className="px-4 py-2.5">When</th>
            <th className="px-4 py-2.5">Level</th>
            <th className="px-4 py-2.5">Code · reason</th>
            <th className="hidden px-4 py-2.5 md:table-cell">Resource</th>
            <th className="hidden px-4 py-2.5 lg:table-cell">Account</th>
            <th className="px-4 py-2.5 text-right" aria-label="External link" />
          </tr>
        </thead>
        <tbody className="divide-y divide-zinc-200 dark:divide-zinc-800/80">
          {rows.map((a) => (
            <AlertRow key={a.id} alert={a} connName={connNameById.get(a.connectionId) ?? "—"} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AlertRow({ alert: a, connName }: { alert: ErrorLogDTO; connName: string }) {
  // Detail row is opt-in: keeps the dense table scannable while still letting
  // operators inspect the full reason + webhook response without leaving the page.
  const [open, setOpen] = useState(false);
  const hasDetail = !!(a.alertText || a.responseBody);
  const rowId = `alert-detail-${a.id}`;

  return (
    <>
      <tr className="transition hover:bg-zinc-50 dark:hover:bg-zinc-900/60">
        <td className="px-4 py-3 align-top">
          <div className="text-[12.5px] text-zinc-900 dark:text-zinc-100">
            {formatRelative(a.dateCreated)}
          </div>
          <div className="font-mono text-[10.5px] text-zinc-400 dark:text-zinc-600">{a.id}</div>
        </td>
        <td className="px-4 py-3 align-top">
          <LevelPill level={a.logLevel} />
        </td>
        <td className="px-4 py-3 align-top">
          <div className="flex items-baseline gap-2">
            {a.errorCode !== null ? (
              <a
                href={`https://www.twilio.com/docs/api/errors/${a.errorCode}`}
                target="_blank"
                rel="noreferrer"
                className="font-mono text-[12.5px] text-brand-600 hover:text-brand-700 dark:text-brand-300 dark:hover:text-brand-200"
              >
                {a.errorCode}
              </a>
            ) : (
              <span className="text-[12px] text-zinc-400 dark:text-zinc-600">—</span>
            )}
            {a.alertText && (
              <span className="max-w-[44ch] truncate text-[12.5px] text-zinc-700 dark:text-zinc-300">
                {a.alertText}
              </span>
            )}
          </div>
          {a.requestMethod && a.requestUrl && (
            <div className="mt-0.5 max-w-[44ch] truncate text-[10.5px] text-zinc-500">
              {a.requestMethod} {a.requestUrl}
            </div>
          )}
          {hasDetail && (
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              aria-expanded={open}
              aria-controls={rowId}
              className="mt-1 text-[11px] font-medium text-zinc-500 hover:text-zinc-700 dark:text-zinc-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-900"
            >
              {open ? "Hide detail" : "Show detail"}
            </button>
          )}
        </td>
        <td className="hidden px-4 py-3 align-top md:table-cell">
          {a.resourceSid ? (
            <span className="font-mono text-[12px] text-zinc-700 dark:text-zinc-300">
              {a.resourceSid}
            </span>
          ) : (
            <span className="text-[12px] text-zinc-400 dark:text-zinc-600">—</span>
          )}
        </td>
        <td className="hidden px-4 py-3 align-top text-[12px] text-zinc-600 dark:text-zinc-400 lg:table-cell">
          {connName}
        </td>
        <td className="px-4 py-3 align-top text-right">
          {a.moreInfo ? (
            <a
              href={a.moreInfo}
              target="_blank"
              rel="noreferrer"
              title="Twilio docs for this alert"
              className="inline-flex items-center gap-1 text-[12px] text-brand-600 hover:text-brand-700 dark:text-brand-300 dark:hover:text-brand-200"
            >
              Docs
              <ExternalLinkIcon size={12} />
            </a>
          ) : (
            <a
              href={twilioAlertConsoleUrl(a)}
              target="_blank"
              rel="noreferrer"
              title="Open in Twilio Console"
              className="inline-flex items-center gap-1 text-[12px] text-brand-600 hover:text-brand-700 dark:text-brand-300 dark:hover:text-brand-200"
            >
              Console
              <ExternalLinkIcon size={12} />
            </a>
          )}
        </td>
      </tr>
      {open && hasDetail && (
        <tr id={rowId} className="bg-zinc-50 dark:bg-zinc-950/40">
          <td colSpan={6} className="px-4 py-4">
            <AlertDetail alert={a} />
          </td>
        </tr>
      )}
    </>
  );
}

function AlertDetail({ alert: a }: { alert: ErrorLogDTO }) {
  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      {a.alertText && (
        <DetailBlock title="Reason">
          <p className="whitespace-pre-wrap break-words text-[12.5px] leading-relaxed text-zinc-800 dark:text-zinc-200">
            {a.alertText}
          </p>
        </DetailBlock>
      )}
      {a.responseBody && (
        <DetailBlock title="Response body (truncated)">
          <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-words rounded-md border border-zinc-200 bg-zinc-50 p-3 font-mono text-[11.5px] leading-relaxed text-zinc-700 dark:border-transparent dark:bg-zinc-950/60 dark:text-zinc-300">
            {a.responseBody}
          </pre>
        </DetailBlock>
      )}
      {a.requestMethod && a.requestUrl && (
        <DetailBlock title="Request">
          <p className="break-all font-mono text-[12px] text-zinc-700 dark:text-zinc-300">
            <span className="text-zinc-500">{a.requestMethod}</span> {a.requestUrl}
          </p>
        </DetailBlock>
      )}
      {a.resourceSid && (
        <DetailBlock title="Resource">
          <p className="font-mono text-[12px] text-zinc-700 dark:text-zinc-300">{a.resourceSid}</p>
        </DetailBlock>
      )}
    </div>
  );
}

function DetailBlock({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="mb-1 text-[11px] font-medium uppercase tracking-wider text-zinc-500">
        {title}
      </div>
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------

function LevelPill({ level }: { level: string | null }) {
  if (!level) return <span className="text-[11.5px] text-zinc-500">—</span>;
  const rose = {
    bg: "bg-rose-50 dark:bg-rose-500/10",
    ring: "ring-rose-500/30",
    text: "text-rose-700 dark:text-rose-300",
    dot: "bg-rose-500 dark:bg-rose-400",
  };
  const amber = {
    bg: "bg-amber-50 dark:bg-amber-500/10",
    ring: "ring-amber-500/30",
    text: "text-amber-700 dark:text-amber-300",
    dot: "bg-amber-500 dark:bg-amber-400",
  };
  const sky = {
    bg: "bg-sky-50 dark:bg-sky-500/10",
    ring: "ring-sky-500/30",
    text: "text-sky-700 dark:text-sky-300",
    dot: "bg-sky-500 dark:bg-sky-400",
  };
  const zinc = {
    bg: "bg-zinc-100 dark:bg-zinc-700/40",
    ring: "ring-zinc-300 dark:ring-zinc-600",
    text: "text-zinc-700 dark:text-zinc-200",
    dot: "bg-zinc-400 dark:bg-zinc-400",
  };
  const styles: Record<string, typeof rose> = {
    error: rose,
    warning: amber,
    notice: sky,
    info: zinc,
  };
  const s = styles[level] ?? {
    bg: "bg-zinc-100 dark:bg-zinc-800/60",
    ring: "ring-zinc-300 dark:ring-zinc-700",
    text: "text-zinc-700 dark:text-zinc-300",
    dot: "bg-zinc-400 dark:bg-zinc-500",
  };
  return (
    <span
      className={
        "inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset " +
        s.bg +
        " " +
        s.ring +
        " " +
        s.text
      }
    >
      <span className={"h-1.5 w-1.5 rounded-full " + s.dot} />
      {level}
    </span>
  );
}

function SkeletonRows() {
  return (
    <div className="overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-card-light dark:border-zinc-800/80 dark:bg-zinc-900/40 dark:shadow-none">
      {Array.from({ length: 8 }).map((_, i) => (
        <div
          key={i}
          className="flex items-center justify-between border-b border-zinc-200 px-4 py-3 last:border-b-0 dark:border-zinc-800/80"
        >
          <div className="space-y-2">
            <div className="h-3 w-24 animate-pulse rounded bg-zinc-200 dark:bg-zinc-800/70" />
            <div className="h-2 w-44 animate-pulse rounded bg-zinc-200 dark:bg-zinc-800/70" />
          </div>
          <div className="flex gap-2">
            <div className="h-4 w-16 animate-pulse rounded-full bg-zinc-200 dark:bg-zinc-800/70" />
            <div className="hidden h-4 w-32 animate-pulse rounded bg-zinc-200 dark:bg-zinc-800/70 md:block" />
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="rounded-xl border border-dashed border-zinc-300 bg-zinc-50 dark:border-zinc-800 dark:bg-zinc-900/30 p-10 text-center">
      <h2 className="text-base font-semibold text-zinc-900 dark:text-zinc-100">No alerts</h2>
      <p className="mx-auto mt-1.5 max-w-md text-[13px] text-zinc-600 dark:text-zinc-400">
        Nothing to see — Twilio hasn't surfaced any alerts for your connected accounts yet. Alerts
        appear here within a few seconds of being raised.
      </p>
    </div>
  );
}

function FilteredEmpty({ onClear }: { onClear: () => void }) {
  return (
    <div className="rounded-xl border border-dashed border-zinc-300 bg-zinc-50 dark:border-zinc-800 dark:bg-zinc-900/30 p-6 text-center">
      <p className="text-[13px] text-zinc-600 dark:text-zinc-400">
        No alerts match the current filter.
      </p>
      <button
        type="button"
        onClick={onClear}
        className="mt-2 text-[12.5px] text-brand-600 hover:text-brand-700 dark:text-brand-300 dark:hover:text-brand-200"
      >
        Clear filters
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------

/**
 * Twilio Console link to the alerts/error-log surface. There's no per-alert
 * deep link in the public console (the SID isn't a URL primitive), so we
 * route to the broader log view scoped to the account where possible.
 */
function twilioAlertConsoleUrl(_a: ErrorLogDTO): string {
  return "https://console.twilio.com/us1/monitor/logs/error-logs";
}

function buildAlertsSummaryCards(s: ErrorLogsSummary): SummaryCard[] {
  return [
    {
      label: "Alerts · 24h",
      value: formatStat(s.total),
      hint: s.total === 0 ? "All clear" : "From Twilio Monitor API",
    },
    {
      label: "Errors · 24h",
      value: formatStat(s.errors),
      hint: s.errors === 0 ? "No errors" : "logLevel = error",
    },
    {
      label: "Warnings · 24h",
      value: formatStat(s.warnings),
      hint: s.warnings === 0 ? "No warnings" : "logLevel = warning",
    },
    {
      label: "Top error code",
      value: s.topErrorCode ? String(s.topErrorCode.code) : "—",
      hint: s.topErrorCode
        ? `${formatStat(s.topErrorCode.count)} occurrence${s.topErrorCode.count === 1 ? "" : "s"}`
        : "No coded alerts",
    },
  ];
}

function formatStat(n: number): string {
  return Math.round(n).toLocaleString("en-US");
}

function formatRelative(iso: string | null): string {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "—";
  const diffMs = Date.now() - then;
  const sec = Math.round(diffMs / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 7) return `${day}d ago`;
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}
